import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useComic } from './ComicContext';
import { useAuth } from './AuthContext';
import { Trash2, Play, Undo2 } from 'lucide-react';
import sairIcon from '../../src/assets/icons/_pub SAIR.png';
import LoadingSpinner from './LoadingSpinner';
import './ImageHotspotEditor.css'
import './LoadingSpinner.css';
import primeira from '../../src/assets/icons/IC PAGE1.png'
import anterior from '../../src/assets/icons/IC PAGE ANT.png'
import proxima from '../../src/assets/icons/IC PROX PAGE .png'
import ultima from '../../src/assets/icons/IC ULT PAGE.png'
import ver from '../../src/assets/icons/IC VIEW.png'
import nao_ver from '../../src/assets/icons/IC NO VIEW.png'
import adicionar from '../../src/assets/icons/IC ADIC.png'
import deletar from './../../src/assets/icons/IC DEL.png'
import salvar from '../../src/assets/icons/IC SAVE.png'
import cancelar from '../../src/assets/icons/IC CANCEL.png'
import publicar from '../../src/assets/icons/IC PUB.png'
import pencilIcon from '../../src/assets/icons/pencil-icon.png'
import balumIcon from '../../src/assets/icons/balum-icon-transparent.png'
import voltar from '../../src/assets/icons/arrow-back.png'
import undoIcon from '../../src/assets/icons/undo-icon.png'

// API functions - add these to your component or a separate api.js file
const BACKEND_ORIGIN = '';
const API_BASE_URL = '/api';

// Image compression utility
const compressImage = (file, options = {}) => {
  return new Promise((resolve, reject) => {
    const {
      maxWidth = 1920,
      maxHeight = 1080,
      quality = 0.8,
      mimeType = 'image/jpeg'
    } = options;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas to Blob conversion failed'));
              return;
            }
            
            // Create new file with compressed image
            const compressedFile = new File([blob], file.name, {
              type: mimeType,
              lastModified: Date.now()
            });
            
            // Log compression results
            const originalSize = file.size;
            const compressedSize = blob.size;
            const compressionRatio = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
            
            console.log('Image compression results:', {
              originalName: file.name,
              originalSize: `${(originalSize / 1024 / 1024).toFixed(2)} MB`,
              compressedSize: `${(compressedSize / 1024 / 1024).toFixed(2)} MB`,
              compressionRatio: `${compressionRatio}%`,
              dimensions: { original: { width: img.width, height: img.height }, compressed: { width, height } }
            });
            
            resolve(compressedFile);
          },
          mimeType,
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
};


// const uploadImage = (file) => {
//   const formData = new FormData();
//   formData.append('file', file);
  
//   return fetch(`${API_BASE_URL}/upload/image`, {
//     method: 'POST',
//     body: formData
//   }).then(res => res.json());
// };

// const uploadVideo = (file) => {
//   const formData = new FormData();
//   formData.append('file', file);
  
//   return fetch(`${API_BASE_URL}/upload/video`, {
//     method: 'POST',
//     body: formData
//   }).then(res => res.json());
// };

const updatePage = async (comicId, pageNum, pageData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/comics/${comicId}/pages/${pageNum}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pageData)
    });
    
    if (response.status === 404) {
      // Comic might not exist in backend, try to create it first
      console.warn(`Comic ${comicId} not found in backend, page save failed`);
      throw new Error(`Comic ${comicId} not found in backend`);
    }
    
    if (!response.ok) {
      throw new Error(`Failed to save page: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error in updatePage:', error);
    throw error;
  }
};

// Utility component for the eye icon
const VisibilityToggle = ({ isVisible, onToggle }) => (
  <button
    onClick={onToggle}
    className={`icon-button visibility-toggle ${!isVisible ? 'hidden' : ''}`}
    title={isVisible ? 'Ocultar' : 'Mostrar'}
  >
    {isVisible ? <img src={ver} className='inside-icon' alt='ver' /> : <img src={nao_ver} className='inside-icon' alt='não ver' />}
  </button>
);

// Utility component for the upload/display box
const ImageLayerBox = ({ title, file, onUpload, onClear, isVisible, onToggleVisibility, accept, allowToggle = true, isLoading = false, isViewer = false }) => (
  <div className="image-layer-box">
    <label className={`image-layer-label ${file ? 'has-file' : ''} ${isLoading ? 'loading' : ''}`}>
      <p className="image-layer-title">{title}</p>
      {isLoading ? (
        <div className="image-layer-loading">
          <LoadingSpinner size={8} />
        </div>
      ) : file ? (
        <p className="image-layer-filename">{file.name}</p>
      ) : (
        <p className="image-layer-placeholder">
          {isViewer ? 'Sem imagem' : 'Clique para upload'}
        </p>
      )}
      {!file && !isLoading && !isViewer && (
        <input type="file" accept={accept} onChange={onUpload} className="image-layer-input" />
      )}
    </label>
    <div className="image-layer-actions">
      {file && !isLoading && !isViewer && (
        <button
          onClick={onClear}
          className="icon-button delete"
          title="Remover imagem"
        >
          <img src={cancelar} className='inside-icon' alt='cancelar' />
        </button>
      )}
      {allowToggle && file && !isLoading && (
        <VisibilityToggle isVisible={isVisible} onToggle={onToggleVisibility} />
      )}
    </div>
  </div>
);


// Utility component for video upload
const VideoUploadBox = ({ title, file, onUpload, onClear, onPreview, isLoading = false }) => (
  <div className="video-upload-box">
    <label className={`image-layer-label ${file ? 'has-file' : ''} ${isLoading ? 'loading' : ''}`}>
      <p className="image-layer-title">{title}</p>
      {isLoading ? (
        <div className="image-layer-loading">
          <LoadingSpinner size={8} />
        </div>
      ) : file ? (
        <p className="image-layer-filename">{file.name}</p>
      ) : (
        <p className="image-layer-placeholder">
          Clique para upload
        </p>
      )}
      {/* Keep the input mounted (but disabled) during upload.
          Unmounting it while a fetch is in flight invalidates the File object
          tied to this input, causing the browser to cancel the request
          (empty status in the Network tab). */}
      {!file && (
        <input type="file" accept="video/*" onChange={onUpload} className="image-layer-input" disabled={isLoading} />
      )}

    </label>
    <div className="image-layer-actions">
      {file && !isLoading && (
        <>
          {/* <button
            onClick={onPreview}
            className="icon-button preview"
            title="Visualizar vídeo (no botão)"
          >
            <Play size={20} />
          </button> */}
          <button
            onClick={onClear}
            className="icon-button delete"
            title="Remover vídeo"
          >
            <Trash2 size={20} />
          </button>
        </>
      )}
    </div>
  </div>
);

export default function ImageHotspotEditor({ onBack }) {
  const {
    currentComic,
    currentPage,
    updateCurrentPage,
    updateSpecificPage,
    updateComicMetadata,
    goToFirstPage,
    goToPreviousPage,
    goToNextPage,
    goToLastPage,
    saveToLocalStorage,
  } = useComic();
  const { logout, isViewer } = useAuth();


  // --- Local State Management ---
  const [bgImage, setBgImage] = useState(null);
  const [ptImage, setPtImage] = useState(null);
  const [lbImage, setLbImage] = useState(null);
  const [ptVisible, setPtVisible] = useState(true);
  const [lbVisible, setLbVisible] = useState(true);
  const [rectangles, setRectangles] = useState([]);
  const [positionsData, setPositionsData] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState(null);
  const [selectedHotspotId, setSelectedHotspotId] = useState(null);
  const [videoFileName, setVideoFileName] = useState('');
  const [videoFiles, setVideoFiles] = useState({});
  const [imageCache, setImageCache] = useState({});
  const [activeVideo, setActiveVideo] = useState(null); // { hotspotId, x, y, videoUrl }
  const [showPublishPreview, setShowPublishPreview] = useState(false);
  // const [previewMode, setPreviewMode] = useState('text'); 

  // --- Undo History State ---
  const [undoStack, setUndoStack] = useState([]);
  const undoStackRef = useRef([]); // Ref to avoid stale closure issues
  
  // Refs to hold latest state values for pushUndoState to avoid stale closures
  const rectanglesRef = useRef(rectangles);
  const positionsDataRef = useRef(positionsData);
  const selectedHotspotIdRef = useRef(selectedHotspotId);
  const videoFilesRef = useRef(videoFiles);
  const videoFileNameRef = useRef(videoFileName);
  const currentRectRef = useRef(currentRect);

  // Keep refs in sync with state
  useEffect(() => { rectanglesRef.current = rectangles; }, [rectangles]);
  useEffect(() => { positionsDataRef.current = positionsData; }, [positionsData]);
  useEffect(() => { selectedHotspotIdRef.current = selectedHotspotId; }, [selectedHotspotId]);
  useEffect(() => { videoFilesRef.current = videoFiles; }, [videoFiles]);
  useEffect(() => { videoFileNameRef.current = videoFileName; }, [videoFileName]);
  useEffect(() => { currentRectRef.current = currentRect; }, [currentRect]);

  // Push current state onto undo stack whenever a tracked action occurs
  const pushUndoState = useCallback(() => {
    const snapshot = {
      rectangles: JSON.parse(JSON.stringify(rectanglesRef.current)),
      positionsData: JSON.parse(JSON.stringify(positionsDataRef.current)),
      selectedHotspotId: selectedHotspotIdRef.current,
      videoFiles: JSON.parse(JSON.stringify(videoFilesRef.current)),
      videoFileName: videoFileNameRef.current,
      currentRect: currentRectRef.current ? JSON.parse(JSON.stringify(currentRectRef.current)) : null,
    };
    undoStackRef.current = [...undoStackRef.current, snapshot];
    // Limit undo stack to 50 entries to prevent memory issues
    if (undoStackRef.current.length > 50) {
      undoStackRef.current = undoStackRef.current.slice(-50);
    }
    setUndoStack(undoStackRef.current);
  }, []); // No dependencies needed since we use refs

  // Undo handler: restores the last saved snapshot
  const handleUndo = useCallback(() => {
    if (isViewer) return; // Viewers cannot undo
    if (undoStackRef.current.length === 0) return;
    const snapshot = undoStackRef.current.pop();
    setUndoStack([...undoStackRef.current]);
    if (snapshot) {
      setRectangles(snapshot.rectangles);
      setPositionsData(snapshot.positionsData);
      setSelectedHotspotId(snapshot.selectedHotspotId);
      setVideoFiles(snapshot.videoFiles);
      setVideoFileName(snapshot.videoFileName);
      setCurrentRect(snapshot.currentRect);
    }
  }, [isViewer]);


  // Clear undo stack when page changes to prevent cross-page undo issues
  useEffect(() => {
    undoStackRef.current = [];
    setUndoStack([]);
  }, [currentPage?.pageNumber]);



  // Keyboard shortcut: Ctrl+Z triggers undo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo]);

  // Inline metadata editing state

  const [editingField, setEditingField] = useState(null); // 'title' | 'author' | 'totalPages'
  const [editingValue, setEditingValue] = useState('');

  // Inline totalPages editing state (for the page number display)
  const [editingTotalPages, setEditingTotalPages] = useState(false);
  const [editingTotalPagesValue, setEditingTotalPagesValue] = useState('');

  const handleStartEdit = (field) => {
    if (isViewer) return; // Viewers cannot edit metadata
    setEditingField(field);
    setEditingValue(String(currentComic.metadata[field]));
  };


  const handleSaveEdit = () => {
    if (!editingField) return;
    const trimmed = editingValue.trim();
    if (!trimmed) { setEditingField(null); return; }
    const value = editingField === 'totalPages' ? parseInt(trimmed) : trimmed;
    if (editingField === 'totalPages' && isNaN(value)) { setEditingField(null); return; }
    updateComicMetadata(currentComic.id, { [editingField]: value });
    setEditingField(null);
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditingValue('');
  };

  const handleStartEditTotalPages = () => {
    if (isViewer) return; // Viewers cannot edit total pages
    setEditingTotalPages(true);
    setEditingTotalPagesValue(String(currentComic.metadata.totalPages));
  };


  const handleSaveEditTotalPages = () => {
    const trimmed = editingTotalPagesValue.trim();
    const value = parseInt(trimmed);
    if (!trimmed || isNaN(value) || value <= 0) {
      setEditingTotalPages(false);
      return;
    }
    updateComicMetadata(currentComic.id, { totalPages: value });
    setEditingTotalPages(false);
  };

  const handleCancelEditTotalPages = () => {
    setEditingTotalPages(false);
    setEditingTotalPagesValue('');
  };

  // Loading states
  const [isUploadingBg, setIsUploadingBg] = useState(false);
  const [isUploadingPt, setIsUploadingPt] = useState(false);
  const [isUploadingLb, setIsUploadingLb] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  // const [isSavingPage, setIsSavingPage] = useState(false);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // Ref to track current page number for upload race condition prevention
  const currentPageNumberRef = useRef(null);
  
  // Keep the ref updated when page changes
  useEffect(() => {
    if (currentPage?.pageNumber !== undefined) {
      currentPageNumberRef.current = currentPage.pageNumber;
    }
  }, [currentPage?.pageNumber]);

  // --- Load current page data from context on mount or page change ---
  useEffect(() => {
    if (currentPage && currentComic) {
      console.log('=== DEBUG: Loading current page data ===');
      console.log('currentPage:', currentPage);
      console.log('currentComic:', currentComic);
      
      // Clear image cache when page changes to prevent showing wrong images
      setImageCache({});
      
      // Reset loading states when page changes to prevent loading indicators from persisting
      setIsUploadingBg(false);
      setIsUploadingPt(false);
      setIsUploadingLb(false);
      setIsUploadingVideo(false);
      
      // Convert backend URLs to file objects - handle database URLs
      const createFileObject = (imageData) => {
        if (!imageData) return null;
        if (imageData.url && imageData.name) {
          // Check if it's already a file object or a backend URL
          if (imageData.url.startsWith('blob:')) {
            return imageData;
          }
          
          // Handle database file URLs (/api/files/{file_id})
          if (imageData.url.startsWith('/api/files/')) {
            const fileId = imageData.url.split('/').pop();
            return {
              name: imageData.name,
              url: `${BACKEND_ORIGIN}${imageData.url}`,
              dbFileId: fileId
            };
          }

          // Handle old filesystem URLs or absolute URLs from other environments
          let resolvedUrl = imageData.url;
          if (resolvedUrl.startsWith('http')) {
            // Strip any known origin and re-apply BACKEND_ORIGIN so local dev works
            const knownOrigins = [
              'https://hq-libras-app-07cb79a3f333.herokuapp.com',
              'https://balumpub.com',
              'http://localhost:5000',
            ];
            for (const origin of knownOrigins) {
              if (resolvedUrl.startsWith(origin)) {
                resolvedUrl = `${BACKEND_ORIGIN}${resolvedUrl.slice(origin.length)}`;
                break;
              }
            }
          } else {
            resolvedUrl = `${BACKEND_ORIGIN}${resolvedUrl}`;
          }
          return { name: imageData.name, url: resolvedUrl };
        }
        return imageData;
      };

      setBgImage(createFileObject(currentPage.bgImage));
      setPtImage(createFileObject(currentPage.ptImage));
      setLbImage(createFileObject(currentPage.lbImage));
      setPtVisible(currentPage.ptVisible !== false);
      setLbVisible(currentPage.lbVisible !== false);
      setRectangles(currentPage.hotspots || []);
      setPositionsData(currentPage.positionsData || []);
      
      // Initialize video files from hotspots
      const videoFilesMap = {};
      (currentPage.hotspots || []).forEach(hotspot => {
        console.log('Processing hotspot:', hotspot.id, 'video:', hotspot.video);
        
        if (hotspot.video) {
          let videoUrl, videoName, dbFileId;
          
          // Check if video is stored in database (starts with /api/files/)
          if (hotspot.video.startsWith('/api/files/')) {
            // Database-stored video
            dbFileId = hotspot.video.split('/').pop();
            videoUrl = `${BACKEND_ORIGIN}${hotspot.video}`;
            videoName = dbFileId; // Use the UUID as name, or get actual filename from backend
            
            console.log('Found database video:', { dbFileId, videoUrl });
          } else {
            // Old filesystem video - try to construct URL
            const comicId = currentComic.id;
            const pageNum = currentPage.pageNumber;
            videoUrl = `${BACKEND_ORIGIN}/uploads/${comicId}/page_${pageNum}/videos/${hotspot.video}`;
            videoName = hotspot.video;
          }
          
          videoFilesMap[hotspot.id] = {
            name: videoName,
            url: videoUrl,
            dbFileId: dbFileId,
            file: new File([], videoName)
          };
        } else {
          console.log('Hotspot has no video:', hotspot.id);
        }
      });
      
      console.log('videoFilesMap created:', videoFilesMap);
      setVideoFiles(videoFilesMap);
    }
  }, [currentPage, currentComic]);





// --- Save current page data to context and backend ---
const savePageData = useCallback(async (showAlert = false) => {
  if (currentComic && currentPage) {
    console.log('=== SAVE PAGE DATA START ===');
    console.log('Comic ID:', currentComic.id);
    console.log('Page Number:', currentPage.pageNumber);
    console.log('Rectangles count:', rectangles.length);
    console.log('Positions data count:', positionsData.length);
    
    // Prepare hotspots with proper video URLs
    const hotspotsWithVideoUrls = rectangles.map(rect => {
      const videoFile = videoFiles[rect.id];
      
      // If we have a video file with a URL, preserve the database URL
      if (videoFile && videoFile.url) {
        // Extract database URL from full URL
        if (videoFile.url.includes('/api/files/')) {
          // It's a database file
          const dbUrl = videoFile.url.split('/api/files/')[1];
          console.log(`Hotspot ${rect.id}: Using database video URL: /api/files/${dbUrl}`);
          return {
            ...rect,
            video: `/api/files/${dbUrl}`  // Store as relative database URL
          };
        } else {
          console.log(`Hotspot ${rect.id}: Using filesystem video: ${videoFile.name}`);
          return {
            ...rect,
            video: videoFile.name
          };
        }
      }
      
      console.log(`Hotspot ${rect.id}: No video attached`);
      return rect;
    });

    // Also update positionsData with proper video values
    const positionsDataWithVideoUrls = positionsData.map(pos => {
      const videoFile = videoFiles[pos.hotspotId];
      
      if (videoFile && videoFile.url) {
        if (videoFile.url.includes('/api/files/')) {
          const dbUrl = videoFile.url.split('/api/files/')[1];
          return {
            ...pos,
            video: `/api/files/${dbUrl}`
          };
        } else {
          return {
            ...pos,
            video: videoFile.name
          };
        }
      }
      
      return pos;
    });

    // Normalize URLs to relative paths before saving so they work across environments
    const toRelativeUrl = (url) => {
      if (!url) return url;
      const origins = [
        'https://hq-libras-app-07cb79a3f333.herokuapp.com',
        'http://localhost:5000',
        window.location.origin,
        BACKEND_ORIGIN,
      ];
      for (const origin of origins) {
        if (url.startsWith(origin)) return url.slice(origin.length);
      }
      return url;
    };

    const pageData = {
      bgImage: bgImage ? { url: toRelativeUrl(bgImage.url) } : null,
      ptImage: ptImage ? { url: toRelativeUrl(ptImage.url) } : null,
      lbImage: lbImage ? { url: toRelativeUrl(lbImage.url) } : null,
      ptVisible: ptVisible,
      lbVisible: lbVisible,
      hotspots: hotspotsWithVideoUrls,
      positionsData: positionsDataWithVideoUrls,
    };

    console.log('Page data to save:', {
      hotspotsCount: pageData.hotspots.length,
      positionsCount: pageData.positionsData.length,
      hasBgImage: !!pageData.bgImage,
      hasPtImage: !!pageData.ptImage,
      hasLbImage: !!pageData.lbImage
    });

    // Update local context
    updateCurrentPage({
      bgImage: bgImage,
      ptImage: ptImage,
      lbImage: lbImage,
      ptVisible: ptVisible,
      lbVisible: lbVisible,
      hotspots: hotspotsWithVideoUrls,
      positionsData: positionsDataWithVideoUrls,
    });
    
    // Save to local storage
    saveToLocalStorage();
    console.log('Data saved to local storage');
    
    // Save to backend
    try {
      console.log('Saving to backend...');
      const response = await updatePage(currentComic.id, currentPage.pageNumber, pageData);
      console.log('Page saved to backend successfully', response);
      
      if (showAlert) {
        alert('Página salva com sucesso no servidor!');
      }
      
      console.log('=== SAVE PAGE DATA END: SUCCESS ===');
      return true;
    } catch (error) {
      console.error('Error saving page to backend:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        comicId: currentComic.id,
        pageNum: currentPage.pageNumber
      });
      
      if (showAlert) {
        alert(`Erro ao salvar página no servidor: ${error.message}. As alterações foram salvas apenas localmente.`);
      }
      
      console.log('=== SAVE PAGE DATA END: FAILED ===');
      return false;
    }
  } else {
    console.warn('Cannot save page: missing currentComic or currentPage');
    console.log('currentComic:', currentComic);
    console.log('currentPage:', currentPage);
    return false;
  }
}, [currentComic, currentPage, bgImage, ptImage, lbImage, ptVisible, lbVisible, rectangles, positionsData, videoFiles, updateCurrentPage, saveToLocalStorage]);



  // Auto-save to backend when data changes - reduced from 1 second to 10 seconds
  useEffect(() => {
    if (isViewer) return; // Viewers should not auto-save
    const timer = setTimeout(() => {
      savePageData();
    }, 10000); // Changed from 1000ms (1 second) to 10000ms (10 seconds)
    return () => clearTimeout(timer);
  }, [rectangles, positionsData, ptVisible, lbVisible, bgImage, ptImage, lbImage, savePageData, isViewer]);

  // --- Navigation Handlers ---
  const handleGoToFirst = useCallback(() => {
    if (!isViewer) savePageData();
    goToFirstPage();
  }, [savePageData, goToFirstPage, isViewer]);

  const handleGoToPrevious = useCallback(() => {
    if (!isViewer) savePageData();
    goToPreviousPage();
  }, [savePageData, goToPreviousPage, isViewer]);

  const handleGoToNext = useCallback(() => {
    if (!isViewer) savePageData();
    goToNextPage();
  }, [savePageData, goToNextPage, isViewer]);

  const handleGoToLast = useCallback(() => {
    if (!isViewer) savePageData();
    goToLastPage();
  }, [savePageData, goToLastPage, isViewer]);


  // --- Image Handlers ---
  const handleImageUpload = useCallback(async (e, setState, imageType, setIsUploading) => {
    if (isViewer) return; // Viewers cannot upload images
    const file = e.target.files?.[0];
    if (file && currentComic && currentPage) {

      // Capture the page number at the start of upload to prevent race conditions
      const uploadStartedForPage = currentPage.pageNumber;
      
      // Set loading state
      if (setIsUploading) setIsUploading(true);
      
      try {
        // Log original file size
        console.log(`Original ${imageType} image:`, {
          name: file.name,
          size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
          type: file.type
        });
        
        let fileToUpload = file;
        
        // Compress image based on type
        try {
          if (imageType === 'bg') {
            // Background JPG: compress more aggressively
            fileToUpload = await compressImage(file, {
              maxWidth: 1920,
              maxHeight: 1080,
              quality: 0.7,
              mimeType: 'image/jpeg'
            });
          } else if (imageType === 'pt' || imageType === 'lb') {
            // Portuguese/Libras PNG: preserve transparency, compress less
            fileToUpload = await compressImage(file, {
              maxWidth: 1920,
              maxHeight: 1080,
              quality: 0.9,
              mimeType: 'image/png'
            });
          }
        } catch (compressionError) {
          console.warn('Image compression failed, using original file:', compressionError);
          // Continue with original file if compression fails
        }
        
        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('page_num', currentPage.pageNumber);
        
        const response = await fetch(`${API_BASE_URL}/upload/image/${currentComic.id}`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const text = await response.text();
          console.error('Upload failed:', response.status, text);
          throw new Error(`Upload failed (${response.status}): ${text.slice(0, 200)}`);
        }
        const data = await response.json();
        if (data.url) {
          const url = `${BACKEND_ORIGIN}${data.url}`;
          const fileObj = { file: fileToUpload, url, name: fileToUpload.name };

          console.log(`Image upload completed for page ${uploadStartedForPage}, current page: ${currentPageNumberRef.current}`);

          if (currentPageNumberRef.current === uploadStartedForPage) {
            
            // Save undo state before clearing hotspots on background upload
            if (imageType === 'bg') {
              pushUndoState();
            }

            setState(fileObj);
            if (imageType === 'bg') {
              setRectangles([]);
              setPositionsData([]);
              setCurrentRect(null);
            }

          } else {



            // User navigated away — write directly into the original page's context data
            const imageField = imageType === 'bg' ? 'bgImage' : imageType === 'pt' ? 'ptImage' : 'lbImage';
            const pageUpdate = { [imageField]: fileObj };
            if (imageType === 'bg') {
              pageUpdate.hotspots = [];
              pageUpdate.positionsData = [];
            }



            updateSpecificPage(uploadStartedForPage, pageUpdate);
            // Also persist to backend immediately so mobile reader can see it
            const backendUpdate = { [imageField]: { url: data.url } };
            if (imageType === 'bg') {
              backendUpdate.hotspots = [];
              backendUpdate.positionsData = [];
            }



            updatePage(currentComic.id, uploadStartedForPage, backendUpdate).catch(err =>
              console.error('Failed to save image URL to backend after navigation:', err)
            );
          }
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Error uploading image. Please try again.');
      } finally {



        // Always clear loading state, regardless of page change
        if (setIsUploading) setIsUploading(false);
      }
    }
  }, [currentComic, currentPage, updateSpecificPage]);


  const handleClearImage = useCallback((state, setState) => {
    if (isViewer) return; // Viewers cannot clear images
    if (state && state.url && state.url.startsWith('blob:')) {
      URL.revokeObjectURL(state.url);
    }
    setState(null);

    if (setState === setBgImage) {
      // Save undo state before clearing hotspots when background is removed
      pushUndoState();
      setRectangles([]);
      setPositionsData([]);
      setCurrentRect(null);
      setSelectedHotspotId(null);
      setVideoFiles({});
      setVideoFileName('');
    }
  }, [pushUndoState]);





  // --- Video Handlers ---
  const handleVideoUpload = useCallback(async (e) => {
    if (isViewer) return; // Viewers cannot upload videos
    const file = e.target.files?.[0];
    if (file && selectedHotspotId && currentComic && currentPage) {

      // Capture the page number at the start of upload to prevent race conditions
      const uploadStartedForPage = currentPage.pageNumber;
      
      setIsUploadingVideo(true);
      try {
        // Create a detached copy of the File so it stays valid even if the
        // <input type="file"> is removed from the DOM mid-upload. A File tied
        // directly to the input can become invalid when the input unmounts,
        // causing the browser to cancel the request (empty status in Network tab).
        const detachedFile = new File([file], file.name, {
          type: file.type,
          lastModified: file.lastModified
        });

        const formData = new FormData();
        formData.append('file', detachedFile);
        formData.append('page_num', currentPage.pageNumber);
        
        const response = await fetch(`${API_BASE_URL}/upload/video/${currentComic.id}`, {
          method: 'POST',
          body: formData
        });

        
        // Check response status BEFORE parsing JSON.
        // Flask returns an HTML error page (not JSON) for 413 Request Entity Too Large,
        // so calling response.json() first would throw a confusing SyntaxError.
        if (!response.ok) {
          let errorMessage = `Upload failed with status: ${response.status}`;
          try {
            const errorData = await response.json();
            if (errorData && errorData.error) {
              errorMessage = errorData.error;
            }
          } catch (parseError) {
            // Response body wasn't JSON (e.g. Flask's HTML 413 page) - keep the status message
            console.warn('Could not parse error response body:', parseError);
          }
          throw new Error(errorMessage);
        }
        
        const data = await response.json();
        
        if (data.url) {

          // Always update the state with the uploaded video
          // The page check was causing issues where videos wouldn't appear when returning to the page
          console.log(`Video upload completed for page ${uploadStartedForPage}, current page: ${currentPageNumberRef.current}`);
          
          // Save undo state before updating video
          pushUndoState();
          
          // Show success message
          alert(`Upload de vídeo concluído com sucesso para a página ${uploadStartedForPage}!`);
          


          
          // The backend returns a database URL like /api/files/{uuid}

          const dbFileUrl = data.url; // e.g., "/api/files/abc-123-def"
          const fullUrl = `${BACKEND_ORIGIN}${dbFileUrl}`;
          
          // Get the file ID (UUID) from the URL
          const fileId = dbFileUrl.split('/').pop(); // "abc-123-def"
          
          console.log('Video uploaded successfully:', {
            dbFileUrl,
            fileId,
            fullUrl,
            response: data
          });
          
          // Update video files state with database reference
          setVideoFiles(prev => ({
            ...prev,
            [selectedHotspotId]: {
              file,
              url: fullUrl,
              dbFileId: fileId,  // Store the database file ID
              name: data.name || file.name
            }
          }));
          
          // Update rectangles with the database URL (not just filename)
          setRectangles(prevRects =>
            prevRects.map(rect =>
              rect.id === selectedHotspotId ? { ...rect, video: dbFileUrl } : rect
            )
          );
          
          // Update positions data with the database URL
          setPositionsData(prevPos =>
            prevPos.map(pos =>
              pos.hotspotId === selectedHotspotId ? { ...pos, video: dbFileUrl } : pos
            )
          );
          
          setVideoFileName(data.name || file.name);
        } else {
          throw new Error('No URL returned from server');
        }
      } catch (error) {
        console.error('Error uploading video:', error);
        alert(`Error uploading video: ${error.message}`);
      } finally {
        // Always clear loading state, regardless of page change
        setIsUploadingVideo(false);
      }
    } else if (!currentComic || !currentPage) {
      alert('Please select a comic and page before uploading video.');
    }
  }, [selectedHotspotId, currentComic, currentPage]);




  const handleClearVideo = useCallback(() => {
    if (isViewer) return; // Viewers cannot clear videos
    if (selectedHotspotId && videoFiles[selectedHotspotId]) {

      // Save undo state before clearing video
      pushUndoState();
      
      const videoFile = videoFiles[selectedHotspotId];
      if (videoFile.url && videoFile.url.startsWith('blob:')) {
        URL.revokeObjectURL(videoFile.url);
      }
      
      // Remove from video files
      setVideoFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[selectedHotspotId];
        return newFiles;
      });
      
      setVideoFileName('');
      
      // Update rectangles
      setRectangles(prevRects =>
        prevRects.map(rect =>
          rect.id === selectedHotspotId ? { ...rect, video: '' } : rect
        )
      );
      
      // Update positions data
      setPositionsData(prevPos =>
        prevPos.map(pos =>
          pos.hotspotId === selectedHotspotId ? { ...pos, video: '' } : pos
        )
      );
    }
  }, [videoFiles, selectedHotspotId, pushUndoState]);


  const handlePreviewVideo = useCallback((hotspotId, event) => {
  console.log('=== DEBUG handlePreviewVideo ===');
  console.log('hotspotId:', hotspotId);
  console.log('videoFiles:', videoFiles);
  console.log('videoFiles[hotspotId]:', videoFiles[hotspotId]);
  
  if (hotspotId && videoFiles[hotspotId] && videoFiles[hotspotId].url) {
    const hotspot = rectangles.find(r => r.id === hotspotId);
    
    if (hotspot) {
      // Get canvas position
      if (canvasRef.current && containerRef.current) {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        const canvasRect = canvas.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        const hotspotCenterX = hotspot.x + (hotspot.width / 2);
        const hotspotCenterY = hotspot.y + (hotspot.height / 2);
        
        const scaleX = canvasRect.width / canvas.width;
        const scaleY = canvasRect.height / canvas.height;
        
        const screenX = canvasRect.left + (hotspotCenterX * scaleX);
        const screenY = canvasRect.top + (hotspotCenterY * scaleY);
        
        const relativeX = screenX - containerRect.left;
        const relativeY = screenY - containerRect.top;
        
        const videoFile = videoFiles[hotspotId];
        let videoUrl = videoFile.url;
        
        // If it's a database file, use the regular endpoint (streaming endpoint has issues)
        if (videoUrl.includes('/api/files/')) {
          // Extract file ID
          const fileId = videoUrl.split('/api/files/')[1];
          // Use regular endpoint instead of streaming endpoint
          videoUrl = `${BACKEND_ORIGIN}/api/files/${fileId}`;
          console.log('Using regular URL for database video:', videoUrl);
        }
        
        const videoName = videoFile.name || '';
        
        // Try to determine MIME type from filename
        let videoType = 'video/mp4'; // default
        
        if (videoName.toLowerCase().endsWith('.webm')) {
          videoType = 'video/webm';
        } else if (videoName.toLowerCase().endsWith('.ogg') || videoName.toLowerCase().endsWith('.ogv')) {
          videoType = 'video/ogg';
        } else if (videoName.toLowerCase().endsWith('.mov')) {
          videoType = 'video/quicktime';
        } else if (videoName.toLowerCase().endsWith('.avi')) {
          videoType = 'video/x-msvideo';
        } else if (videoName.toLowerCase().endsWith('.wmv')) {
          videoType = 'video/x-ms-wmv';
        } else if (videoName.toLowerCase().endsWith('.mkv')) {
          videoType = 'video/x-matroska';
        }
        
        console.log('Video preview settings:', { 
          videoUrl, 
          videoName, 
          videoType,
          originalUrl: videoFiles[hotspotId].url 
        });
        
        const FLOATING_VIDEO_W = 360;
        const FLOATING_VIDEO_H = 202;
        const MARGIN = 8;
        let left = relativeX - FLOATING_VIDEO_W / 2;
        let top = relativeY - FLOATING_VIDEO_H - 10;
        if (left < MARGIN) left = MARGIN;
        if (left + FLOATING_VIDEO_W > containerRect.width - MARGIN) left = containerRect.width - FLOATING_VIDEO_W - MARGIN;
        if (top < MARGIN) top = MARGIN;
        if (top + FLOATING_VIDEO_H > containerRect.height - MARGIN) top = containerRect.height - FLOATING_VIDEO_H - MARGIN;

        setActiveVideo({
          hotspotId,
          x: relativeX,
          y: relativeY,
          left,
          top,
          videoUrl: videoUrl,
          videoName: videoName,
          videoType: videoType
        });
        
        if (event) {
          event.stopPropagation();
        }
      }
    }
  } else {
    console.error('Cannot preview video: missing data');
  }
}, [videoFiles, rectangles]);




  const handleCloseVideoPreview = useCallback((event) => {
    setActiveVideo(null);
    if (event) {
      event.stopPropagation();
    }
  }, []);

// Update the click outside useEffect
useEffect(() => {
  let clickTimer;
  
  const handleClickOutside = (e) => {
    if (!activeVideo) return;
    
    // Check if click is on video bubble or its children
    const videoBubble = document.querySelector('.video-bubble');
    const isClickOnVideoBubble = videoBubble && 
      (videoBubble === e.target || videoBubble.contains(e.target));
    
    // Check if click is on canvas
    const isClickOnCanvas = canvasRef.current && 
      canvasRef.current.contains(e.target);
    
    // Check if click is on preview button in table
    const isClickOnPreviewButton = e.target.closest('.preview-button');
    
    // Only close if clicking outside all these elements
    if (!isClickOnVideoBubble && !isClickOnCanvas && !isClickOnPreviewButton) {
      // Clear any existing timer
      clearTimeout(clickTimer);
      
      // Add small delay to prevent accidental closing
      clickTimer = setTimeout(() => {
        handleCloseVideoPreview();
      }, 100);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
    clearTimeout(clickTimer);
  };
}, [activeVideo, handleCloseVideoPreview]);




// --- Canvas Drawing Logic ---
useEffect(() => {
  if (!canvasRef.current || !bgImage) return;

  const canvas = canvasRef.current;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Function to load and cache image with better error handling
  const loadImage = (url, cacheKey) => {
    return new Promise((resolve, reject) => {
      // Check cache first
      if (imageCache[cacheKey]) {
        resolve(imageCache[cacheKey]);
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      // Set timeout for image loading
      const timeoutId = setTimeout(() => {
        reject(new Error(`Image load timeout for ${cacheKey}`));
      }, 10000); // 10 second timeout
      
      img.onload = () => {
        clearTimeout(timeoutId);
        // Cache the image
        setImageCache(prev => ({ ...prev, [cacheKey]: img }));
        resolve(img);
      };
      
      img.onerror = (error) => {
        clearTimeout(timeoutId);
        console.error(`Error loading image ${cacheKey} from ${url}:`, error);
        reject(new Error(`Failed to load image: ${cacheKey}`));
      };
      
      img.src = url;
    });
  };

  const draw = async () => {
    if (!bgImage || !bgImage.url) return;

    // Clear canvas once at the beginning
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    try {
      // Load and draw background
      const bgImg = await loadImage(bgImage.url, 'bg');
      
      // Set canvas dimensions to match image
      if (canvas.width !== bgImg.width || canvas.height !== bgImg.height) {
        canvas.width = bgImg.width;
        canvas.height = bgImg.height;
      }
      
      // Draw background
      ctx.drawImage(bgImg, 0, 0);

      // Draw Portuguese layer if visible
      if (ptImage && ptImage.url && ptVisible) {
        const ptImg = await loadImage(ptImage.url, 'pt');
        ctx.drawImage(ptImg, 0, 0, canvas.width, canvas.height);
      }

      // Draw Libras layer if visible
      if (lbImage && lbImage.url && lbVisible) {
        const lbImg = await loadImage(lbImage.url, 'lb');
        ctx.drawImage(lbImg, 0, 0, canvas.width, canvas.height);
      }

      // Draw all rectangles
      drawRectangles(ctx);
      
    } catch (error) {
      console.error('Error drawing images:', error);
    }
  };

  const drawRectangles = (ctx) => {
  rectangles.forEach((rect) => {
    const isSelected = rect.id === selectedHotspotId;
    const isPlaying = activeVideo && activeVideo.hotspotId === rect.id;
    const position = positionsData.find((p) => p.hotspotId === rect.id);
    const videoValue = (position && position.video) || rect.video || '';
    const hasVideo = videoValue.trim() !== '';

    // Choose color based on video status
    let strokeColor, fillColor;
    
    if (isPlaying) {
      // Rectangle with active video: blue
      strokeColor = '#3b82f6';
      fillColor = 'rgba(59, 130, 246, 0.2)';
    } else if (hasVideo) {
      // Rectangle with video: green
      strokeColor = '#10b981';
      fillColor = 'rgba(16, 185, 129, 0.1)';
    } else {
      // Rectangle without video: red
      strokeColor = '#ef4444';
      fillColor = 'rgba(239, 68, 68, 0.1)';
    }

    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = isSelected ? 3 : 2;
    ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    ctx.fillStyle = fillColor;
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  });

  if (currentRect) {
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.strokeRect(currentRect.x, currentRect.y, currentRect.width, currentRect.height);
    ctx.fillStyle = 'rgba(239, 68, 68, 0.1)';
    ctx.fillRect(currentRect.x, currentRect.y, currentRect.width, currentRect.height);
  }
};

  draw();

  // Clean up function to prevent memory leaks
  return () => {
    // Don't clear context here
  };




}, [bgImage, ptImage, lbImage, ptVisible, lbVisible, rectangles, currentRect, selectedHotspotId, positionsData, imageCache, activeVideo]);





  // --- Canvas Interaction ---
  const getCanvasCoordinates = (e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    return { x, y };
  };

  const handleMouseDown = (e) => {
  if (!bgImage) return;
  if (isViewer) return; // Viewers cannot draw hotspots
  
  const pos = getCanvasCoordinates(e);

  
  // Check if clicking on existing hotspot
  const clickedHotspot = rectangles.find(rect =>
    pos.x >= rect.x &&
    pos.x <= rect.x + rect.width &&
    pos.y >= rect.y &&
    pos.y <= rect.y + rect.height
  );

  if (clickedHotspot) {
    // Prevent default and stop propagation to avoid immediate closing
    e.preventDefault();
    e.stopPropagation();
    
    const hasVideo = videoFiles[clickedHotspot.id] && videoFiles[clickedHotspot.id].url;
    
    if (hasVideo) {
      // If clicking on the same hotspot that's already playing, do nothing
      if (activeVideo && activeVideo.hotspotId === clickedHotspot.id) {
        return;
      }
      
      // Close any existing video first
      if (activeVideo) {
        handleCloseVideoPreview();
      }
      
      // Small delay to ensure previous video is closed
      setTimeout(() => {
        handlePreviewVideo(clickedHotspot.id, e);
      }, 50);
    } else {
      // If no video, just select the hotspot
      handleSelectHotspot(clickedHotspot.id);
    }
    return;
  }

  // If clicking outside hotspots while video is playing, close it
  if (activeVideo) {
    handleCloseVideoPreview();
  }

  // If there's a pending rectangle (drawn but not yet added as a hotspot),
  // clicking on empty canvas dismisses it instead of starting a new draw.
  if (currentRect) {
    setCurrentRect(null);
    return;
  }

  // Start drawing new rectangle
  setIsDrawing(true);
  setStartPos(pos);
};





  const handleMouseMove = (e) => {
  if (!isDrawing) return;
  
  const pos = getCanvasCoordinates(e);
  const width = pos.x - startPos.x;
  const height = pos.y - startPos.y;

  setCurrentRect({
    id: 'temp',
    x: width < 0 ? pos.x : startPos.x,
    y: height < 0 ? pos.y : startPos.y,
    width: Math.abs(width),
    height: Math.abs(height),
  });
};

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    // Don't clear currentRect here - let it persist until user adds or cancels
  };

  // --- Hotspot Management ---
  const handleAddHotspot = () => {
    if (isViewer) return; // Viewers cannot add hotspots
    if (!currentRect || currentRect.width < 5 || currentRect.height < 5) {

      alert('Por favor, desenhe um retângulo válido (mínimo 5x5 pixels) antes de adicionar.');
      return;
    }

    const newHotspotId = Date.now().toString();

    const newHotspot = {
      id: newHotspotId,
      x: currentRect.x,
      y: currentRect.y,
      width: currentRect.width,
      height: currentRect.height,
      video: '',
    };

    const newPosition = {
      id: positionsData.length + 1,
      hotspotId: newHotspotId,
      coords: `${Math.round(currentRect.x)}, ${Math.round(currentRect.y)}`,
      label: `H01_P${String(currentPage.pageNumber).padStart(2, '0')}_Pn${positionsData.length + 1}`,
      video: '',
    };

    pushUndoState();
    setRectangles((prevRects) => [...prevRects, newHotspot]);
    setPositionsData((prevPos) => [...prevPos, newPosition]);
    
    // Clear current rect AFTER adding
    setCurrentRect(null);
    
    setSelectedHotspotId(newHotspotId);
    setVideoFileName('');

  };

  const handleDeleteHotspot = (hotspotId) => {
    if (isViewer) return; // Viewers cannot delete hotspots
    if (!hotspotId) {

      if (rectangles.length === 0) {
        alert('Nenhuma marcação para deletar.');
        return;
      }
      // If no hotspotId provided, use the selected one or the last one
      hotspotId = selectedHotspotId || positionsData[positionsData.length - 1]?.hotspotId;
      if (!hotspotId) return;
    }
    
    pushUndoState();
    setRectangles((prevRects) => prevRects.filter(rect => rect.id !== hotspotId));
    setPositionsData((prevPos) => prevPos.filter(pos => pos.hotspotId !== hotspotId));
    
    // Clean up video file if exists

    if (videoFiles[hotspotId]) {
      const videoFile = videoFiles[hotspotId];
      if (videoFile.url && videoFile.url.startsWith('blob:')) {
        URL.revokeObjectURL(videoFile.url);
      }
      setVideoFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[hotspotId];
        return newFiles;
      });
    }
    
    setSelectedHotspotId(null);
    setVideoFileName('');
  };


  const handleSelectHotspot = (hotspotId) => {
    setSelectedHotspotId(hotspotId);
    const selectedRect = rectangles.find(r => r.id === hotspotId);
    setVideoFileName(selectedRect ? selectedRect.video : '');
  };

  const handleVideoFileNameChange = (e) => {
    const newFileName = e.target.value;
    setVideoFileName(newFileName);

    if (selectedHotspotId) {
      // Save undo state before changing video file name
      pushUndoState();
      
      setRectangles(prevRects =>
        prevRects.map(rect =>
          rect.id === selectedHotspotId ? { ...rect, video: newFileName } : rect
        )
      );

      setPositionsData(prevPos =>
        prevPos.map(pos =>
          pos.hotspotId === selectedHotspotId ? { ...pos, video: newFileName } : pos
        )
      );
    }
  };


  // --- Publish Handler ---
  const handlePublish = useCallback(async () => {
    if (isViewer) return; // Viewers cannot publish
    if (currentComic) {

      // Validate every page has a background image
      const missingPages = [];
      for (let i = 0; i <= currentComic.metadata.totalPages; i++) {
        const page = currentComic.pages[i];
        if (!page || !page.bgImage) {
          missingPages.push(i === 0 ? 'Capa' : `Página ${i}`);
        }
      }
      if (missingPages.length > 0) {
        alert(`Não é possível publicar: as seguintes páginas não têm imagem:\n\n${missingPages.join('\n')}`);
        return;
      }

      try {
        console.log('=== PUBLISH: Saving page data before publishing ===');
        await savePageData(true);
        console.log('=== PUBLISH: Page data saved successfully ===');
        setShowPublishPreview(true);
      } catch (error) {
        console.error('Error saving data before publish:', error);
        alert('Erro ao salvar dados antes de publicar. Verifique se todos os dados foram salvos.');
      }
    }
  }, [currentComic, savePageData]);

  // --- Finalize Publish Handler ---
  const handleFinalizePublish = useCallback(async () => {
    if (isViewer) return; // Viewers cannot publish
    if (currentComic) {

      try {
        console.log('=== FINALIZE PUBLISH: Publishing comic', currentComic.id, '===');
        
        // Double-check that data is saved
        await savePageData();
        
        // Now publish the comic
        const response = await fetch(`${API_BASE_URL}/comics/${currentComic.id}/publish`, {
          method: 'POST',
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log('Publish successful:', result);
          alert('Quadrinho publicado com sucesso! Status alterado para Produção.');
          setShowPublishPreview(false);
          
          // Refresh comic data to show updated status
          // You might want to add a refresh function to ComicContext
        } else {
          const errorText = await response.text();
          console.error('Publish failed:', response.status, errorText);
          alert(`Erro ao publicar quadrinho: ${response.status}. Verifique o console para mais detalhes.`);
        }
      } catch (error) {
        console.error('Error publishing comic:', error);
        alert(`Erro ao publicar quadrinho: ${error.message}. Tente novamente.`);
      }
    }
  }, [currentComic, savePageData]);

  // --- Unpublish Handler ---
  const handleUnpublish = useCallback(async () => {
    if (!currentComic) return;
    if (!window.confirm('Tem certeza que deseja despublicar este quadrinho? O status voltará para Desenvolvimento.')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/comics/${currentComic.id}/unpublish`, { method: 'POST' });
      if (response.ok) {
        updateComicMetadata(currentComic.id, { status: 'development' });
        alert('Quadrinho despublicado. Status alterado para Desenvolvimento.');
      } else {
        alert(`Erro ao despublicar quadrinho: ${response.status}.`);
      }
    } catch (error) {
      alert(`Erro ao despublicar quadrinho: ${error.message}.`);
    }
  }, [currentComic, updateComicMetadata]);

  // --- Close Preview Handler ---
  const handleClosePreview = useCallback(() => {
    setShowPublishPreview(false);
    // setPreviewMode('text');
  }, []);

  // --- Check if we have comic data ---
  if (!currentComic || !currentPage) {
    return (
      <div className="image-hotspot-editor">
        <div className="editor-container">
          <div className="loading">Carregando...</div>
        </div>
      </div>
    );
  }

  // --- Render ---
  return (
    <>
      <div className="image-hotspot-editor">
        <div className="logo clickable editor-logo" onClick={onBack}>
          <img src={balumIcon} alt="balum" className="logo-img" />
        </div>
        <button onClick={onBack} className="logout-button" title="Voltar para a página inicial">
          {/* <img src={sairIcon} alt="Sair" style={{ width: 30, height: 30 }} /> */}
          <img src={cancelar} alt="Página inicial" className='out-editor-button' style={{ width: 40, height: 40 }} />
        </button>
        <div className="editor-container">

          {/* Left Column: Controls and Metadata */}
          <div className="left-column">

            {/* Page Info */}
            <div className="page-info">
              {[
                { field: 'title', label: 'TÍTULO' },
                { field: 'author', label: 'AUTOR' },
                { field: 'category', label: 'CATEGORIA' },
              ].map(({ field, label }) => (
                <p key={field} className="page-info-text">
                  <strong>{label}:</strong>{' '}
                  {editingField === field ? (
                    <input
                      type="text"
                      value={editingValue}
                      onChange={e => setEditingValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                      onBlur={handleSaveEdit}
                      className="page-info-edit-input"
                      autoFocus
                    />
                  ) : (
                    <span className="page-info-value" onClick={() => handleStartEdit(field)} title={isViewer ? undefined : "Clique para editar"}>
                      {currentComic.metadata[field]}
                      {!isViewer && <img src={pencilIcon} alt="editar" className="page-info-pencil" />}
                    </span>
                  )}

                </p>
              ))}
            </div>

            {/* Page Navigation */}
            <div className="page-navigation">
              <div className="page-number">
                {currentPage.pageNumber === 0 ? (
                  'CAPA'
                ) : editingTotalPages ? (
                  <span className="page-number-editing">
                    {currentPage.pageNumber}/
                    <input
                      type="number"
                      value={editingTotalPagesValue}
                      onChange={e => setEditingTotalPagesValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveEditTotalPages(); if (e.key === 'Escape') handleCancelEditTotalPages(); }}
                      onBlur={handleSaveEditTotalPages}
                      className="page-number-edit-input"
                      autoFocus
                      min="1"
                    />
                  </span>
                ) : (
                  <span className="page-number-value" onClick={handleStartEditTotalPages} title={isViewer ? undefined : "Clique para editar número de páginas"}>
                    {currentPage.pageNumber}/{currentComic.metadata.totalPages}
                    {!isViewer && <img src={pencilIcon} alt="editar" className="page-info-pencil" />}
                  </span>
                )}

              </div>
              <div className="nav-buttons">
                <button className="nav-button" title="Primeira" onClick={handleGoToFirst}>
                  <img src={primeira} alt='primeira' />
                </button>
                <button className="nav-button" title="Anterior" onClick={handleGoToPrevious}>
                  <img src={anterior} alt='anterior' />
                </button>
                <button className="nav-button" title="Próxima" onClick={handleGoToNext}>
                  <img src={proxima} alt='proxima' />
                </button>
                <button className="nav-button" title="Última" onClick={handleGoToLast}>
                  <img src={ultima} alt='ultima' />
                </button>
              </div>
            </div>

            {/* Image and Video Upload Buttons */}
            <div className='upload-buttons-container'>
              <ImageLayerBox
                title="BACKGROUND (JPG)"
                file={bgImage}
                onUpload={(e) => handleImageUpload(e, setBgImage, 'bg', setIsUploadingBg)}
                onClear={() => handleClearImage(bgImage, setBgImage)}
                isVisible={true}
                onToggleVisibility={() => {}}
                accept="image/jpeg,image/jpg"
                allowToggle={false}
                isLoading={isUploadingBg}
                isViewer={isViewer}
              />

              <ImageLayerBox
                title="PORTUGUÊS (PNG)"
                file={ptImage}
                onUpload={(e) => handleImageUpload(e, setPtImage, 'pt', setIsUploadingPt)}
                onClear={() => handleClearImage(ptImage, setPtImage)}
                isVisible={ptVisible}
                onToggleVisibility={() => setPtVisible(!ptVisible)}
                accept="image/png"
                isLoading={isUploadingPt}
                isViewer={isViewer}
              />

              <ImageLayerBox
                title="LIBRAS (PNG)"
                file={lbImage}
                onUpload={(e) => handleImageUpload(e, setLbImage, 'lb', setIsUploadingLb)}
                onClear={() => handleClearImage(lbImage, setLbImage)}
                isVisible={lbVisible}
                onToggleVisibility={() => setLbVisible(!lbVisible)}
                accept="image/png"
                isLoading={isUploadingLb}
                isViewer={isViewer}
              />

              {/* Video Upload Section - only show when no video is associated with the selected hotspot */}
              {!isViewer && selectedHotspotId && !videoFiles[selectedHotspotId] && (
                <>
                  <VideoUploadBox   
                    title={`VÍDEO (${positionsData.find(p => p.hotspotId === selectedHotspotId)?.label || ''})`}
                    file={selectedHotspotId ? videoFiles[selectedHotspotId] : null}
                    onUpload={handleVideoUpload}
                    onClear={handleClearVideo}
                    onPreview={handlePreviewVideo}
                    isLoading={isUploadingVideo}
                  />
                </>
              )}


            </div>

            {!isViewer && (
              <div className='video-buttons-container'>

                <div>
                    <button
                          onClick={handleAddHotspot}
                          disabled={!currentRect}
                          className="action-button add"
                          title="Adicionar Marcação"
                        >
                      <img src={adicionar} alt="adicionar" />
                    </button>

                </div>

                <div>
                    <button
                      onClick={() => handleDeleteHotspot()}
                      disabled={rectangles.length === 0}
                      className="action-button delete"
                      title={selectedHotspotId ? "Deletar Marcação Selecionada" : "Deletar Última Marcação"}
                    >
                      <img src={deletar} alt="deletar" />
                    </button>
                </div>



              </div>
            )}


            {/* Positions and Video Editor */}
            <div className="positions-video-editor">

              
              {/* Positions Table */}
              <div className="positions-table-container">
                <div className="positions-table-header">
                  <h3 className="positions-table-title">POSIÇÕES</h3>
                  <h3 className="positions-table-title">VÍDEO</h3>
                </div>
                <div className="positions-table-scroll">
                  <table className="positions-table">

                    <thead>
                      <tr>
                        <th>#</th>
                        <th className="coordinates">Coordenadas</th>
                        <th>Label</th>
                        {/* <th>Vídeo</th> */}
                        <th>Preview</th>
                        <th></th>
                      </tr>
                    </thead>


                    <tbody>
                      {positionsData.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="empty-positions">Nenhuma marcação adicionada.</td>
                        </tr>
                      ) : (
                        positionsData.map((pos) => {
                          const hasVideo = (pos.video && pos.video.trim() !== '') || !!(videoFiles[pos.hotspotId]?.url);
                          return (
                            <tr
                              key={pos.id}
                              onClick={() => handleSelectHotspot(pos.hotspotId)}
                              className={`position-row ${selectedHotspotId === pos.hotspotId ? 'selected' : ''} ${hasVideo ? 'has-video' : 'no-video'}`}
                            >
                              <td className="number">{pos.id}</td>
                              <td className="coordinates coords">{pos.coords}</td>
                              <td className="label">{pos.label}</td>
                              {/* <td className="video">{pos.video || '-'}</td> */}
                              <td className="preview-cell">
                                {hasVideo && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePreviewVideo(pos.hotspotId, e);
                                    }}
                                    className="preview-button"
                                    title="Visualizar vídeo"
                                  >
                                    <Play size={16} />
                                  </button>
                                )}
                              </td>
                              {/* <td className="delete-cell">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteHotspot(pos.hotspotId);
                                  }}
                                  className="icon-button delete"
                                  title="Deletar marcação"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td> */}
                            </tr>
                          );
                        })
                      )}
                    </tbody>




                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Preview/Editor */}
          <div className="right-column">



            <div className='canvas-editor-container'>

              {/* Preview Header */}
              <div className="preview-header">
                <h2 className="preview-title">PREVIEW</h2>
                <p className="preview-markers-count">no. de marcações: <span className="preview-markers-number">{positionsData.length}</span></p>
              </div>

              {/* Canvas Editor and Action Buttons */}
              <div className="canvas-editor">

                {/* Canvas Container */}
                <div className="canvas-container">
                  {!bgImage ? (
                    isViewer ? (
                      <div className="canvas-placeholder">
                        <span>Sem imagem de fundo disponível</span>
                      </div>
                    ) : (
                      <label className="canvas-placeholder clickable">
                        <span>Faça upload da imagem de fundo para começar</span>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg"
                          onChange={(e) => handleImageUpload(e, setBgImage, 'bg')}
                          style={{ display: 'none' }}
                        />
                      </label>
                    )
                  ) : (

                    <div ref={containerRef} className="canvas-wrapper">
                      <canvas
                        ref={canvasRef}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        className="canvas"
                      />
                      
                      {/* Floating Video Player */}
                      {activeVideo && (
                        <div
                          className="floating-video"
                          style={{ left: `${activeVideo.left}px`, top: `${activeVideo.top}px` }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <video
                            controls
                            controlsList="nodownload nofullscreen noremoteplayback"
                            disablePictureInPicture
                            autoPlay
                            className="floating-video-player"
                            onEnded={handleCloseVideoPreview}
                            preload="auto"
                          >
                            <source src={activeVideo.videoUrl} type={activeVideo.videoType} />
                            {activeVideo.videoType !== 'video/mp4' && (
                              <source src={activeVideo.videoUrl.replace(/\.[^.]+$/, '.mp4')} type="video/mp4" />
                            )}
                          </video>
                        </div>
                      )}



                    </div>
                  )}
                </div>



                {/* Action Buttons */}
                {!isViewer && (
                  <div className="action-buttons">
                    
                    
                    {/* tirei os botões de mais e menos daqui */}

                    <div className="divider"></div>

                    <button 
                      className="action-button save" 
                      title="Salvar" 
                      onClick={savePageData}
                    >
                      <img src={salvar} alt='salvar' />
                    </button>

                    <button 
                      className="action-button undo" 
                      title="Desfazer (Ctrl+Z)" 
                      onClick={handleUndo}
                      disabled={undoStack.length === 0}
                    >
                      <img src={undoIcon} alt='desfazer' />
                    </button>

                    <button

                      onClick={handleUnpublish}
                      disabled={currentComic.metadata.status !== 'production'}
                      className="action-button cancel"
                      title="Despublicar"
                    >
                      <img src={cancelar} alt='despublicar' />
                    </button>

                    <button 
                      className="action-button publish" 
                      title="Publicar"
                      onClick={handlePublish}
                    >
                      <img src={publicar} alt='publicar' />
                    </button>
                  </div>
                )}


              </div>

            </div>

          </div>
        </div>
      </div>

      {/* Publish Preview Modal */}
      {showPublishPreview && (
        <div className="publish-preview-modal">
          <div className="publish-preview-content">


            <div className="publish-preview-header">
              <h2 className="publish-preview-title">Publicação do Quadrinho</h2>
              <button 
                onClick={handleClosePreview}
                className="publish-preview-close"
              >
                ×
              </button>
            </div>
            
            <div className="publish-preview-body">

              <div className="preview-mode-toggle">

                {/* <button
                  onClick={() => setPreviewMode('text')}
                  className={`preview-mode-button ${previewMode === 'text' ? 'active' : ''}`}
                >
                  Modo Texto
                </button> */}

                {/* <button
                  onClick={() => setPreviewMode('video')}
                  className={`preview-mode-button ${previewMode === 'video' ? 'active' : ''}`}
                >
                  Modo Vídeo
                </button> */}

              </div>

              <div className="preview-comic-info">
                <h3>{currentComic.metadata.title}</h3>
                <p>Autor: {currentComic.metadata.author}</p>
                <p>Páginas: {currentComic.metadata.totalPages}</p>
                <p>Status atual: <span className={`status-badge status-${currentComic.metadata.status || 'development'}`}>
                  {currentComic.metadata.status === 'production' ? 'Produção' : 'Desenvolvimento'}
                </span></p>
              </div>

              <div className="preview-navigation">
                {/* <div className="preview-page-info">
                  <p>Navegue pelas páginas para verificar o conteúdo:</p>
                  <div className="preview-nav-buttons">
                    <button className="nav-button" title="Primeira" onClick={handleGoToFirst}>
                      <img src={primeira} alt='primeira' />
                    </button>
                    <button className="nav-button" title="Anterior" onClick={handleGoToPrevious}>
                      <img src={anterior} alt='anterior' />
                    </button>
                    <span className="page-number">
                      {currentPage.pageNumber}/{currentComic.metadata.totalPages}
                    </span>
                    <button className="nav-button" title="Próxima" onClick={handleGoToNext}>
                      <img src={proxima} alt='proxima' />
                    </button>
                    <button className="nav-button" title="Última" onClick={handleGoToLast}>
                      <img src={ultima} alt='ultima' />
                    </button>
                  </div>
                </div> */}

                {/* <div className="preview-canvas-container">
                  <div className="canvas-wrapper">
                    <canvas
                      ref={canvasRef}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}  
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}   
                      className="canvas"
                    />
                                        
                    {previewMode === 'video' && activeVideo && (
                      <div 
                        className="video-bubble"
                        style={{
                          position: 'absolute',
                          left: `${activeVideo.x}px`,
                          top: `${activeVideo.y}px`,
                          transform: 'translate(-50%, -100%)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="video-bubble-tail"></div>
                        <div className="video-bubble-content">
                          <div className="video-bubble-header">
                            <span className="video-bubble-title">
                              {positionsData.find(p => p.hotspotId === activeVideo.hotspotId)?.label || 'Video'}
                            </span>
                            <button 
                              onClick={handleCloseVideoPreview}
                              className="video-bubble-close"
                            >
                              ×
                            </button>
                          </div>
                          <video 
                            controls 
                            controlsList="nodownload nofullscreen noremoteplayback" 
                            disablePictureInPicture
                            autoPlay 
                            className="video-bubble-player"
                            onEnded={handleCloseVideoPreview}
                            onError={(e) => {
                              console.error('Preview video error:', e);
                            }}
                          >
                            <source src={activeVideo.videoUrl} type={activeVideo.videoType || 'video/mp4'} />
                            {activeVideo.videoType !== 'video/webm' && (
                              <source src={activeVideo.videoUrl.replace(/\.[^.]+$/, '.webm')} type="video/webm" />
                            )}
                            Seu navegador não suporta a tag de vídeo.
                          </video>
                        </div>
                      </div>
                    )}
                  </div>
                </div> */}



                {/* <div className="preview-mode-description">
                  {previewMode === 'text' ? (
                    <p>Modo Texto: Mostrando camada em Português. Clique nos botões para alternar para o modo Vídeo.</p>
                  ) : (
                    <p>Modo Vídeo: Clique nos retângulos verdes para reproduzir os vídeos em Libras.</p>
                  )}
                </div> */}
              </div>


              

              <div className="publish-preview-actions">
                <button
                  onClick={handleClosePreview}
                  className="preview-action-button cancel"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleFinalizePublish}
                  className="preview-action-button publish"
                >
                  Publicar Quadrinho
                </button>
              </div>

              <div className="publish-note">
                <p><strong>Nota:</strong> Ao publicar, o status do quadrinho será alterado para "Produção" e estará pronto para ser lido no aplicativo.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}